import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Chip, CircularProgress, Alert, Menu, useTheme, alpha,
} from '@mui/material';
import {
  Add as AddIcon, FormatListBulleted as ListIcon,
  Edit as EditIcon, Archive as ArchiveIcon, ArrowDropDown as DropdownIcon,
  ArrowForward as ArrowForwardIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import wineListService from '../../../../services/wineListService';
import type { WineList, WineListStatus, WineListType } from '../../../../types/venue';

const STATUS_COLORS: Record<WineListStatus, 'default' | 'primary' | 'success' | 'warning'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  ARCHIVED: 'warning',
};

const WINE_LIST_TYPES: WineListType[] = ['FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE'];
const WINE_LIST_STATUSES: WineListStatus[] = ['DRAFT', 'ACTIVE', 'ARCHIVED'];

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

const WineListList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const base = `/admin/venues/${venueId}/wine-lists`;

  const [items, setItems] = useState<WineList[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25, totalItems: 0 });
  // Phase 4.I — soft-redirect: the legacy table is collapsed by default and
  // the page acts as a redirect surface. The operator must explicitly opt in
  // to see the legacy data. Fetching is gated so the list endpoint isn't hit
  // while collapsed.
  const [showLegacyTable, setShowLegacyTable] = useState(false);

  const fetchItems = useCallback(async (type: string, status: string, page: number, pageSize: number) => {
    setLoading(true);
    setFetchError('');
    try {
      const result = await wineListService.getWineLists(venueId, page + 1, pageSize, type || undefined, status || undefined);
      setItems(result.data);
      setPagination(prev => ({ ...prev, page, pageSize, totalItems: result.pagination.totalItems }));
    } catch (err) {
      console.error('Failed to load wine lists', err);
      setFetchError('Could not load wine lists. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    // Phase 4.I — only fetch when the operator has opted into the legacy
    // table. Avoids an unnecessary network call on the redirect-only landing.
    if (!showLegacyTable) return;
    fetchItems(typeFilter, statusFilter, pagination.page, pagination.pageSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLegacyTable, typeFilter, statusFilter, pagination.page, pagination.pageSize]);

  const handleArchive = async (e: React.MouseEvent, list: WineList) => {
    e.stopPropagation();
    if (!window.confirm(`Archive wine list "${list.name}"? Its items will be preserved.`)) return;
    try {
      await wineListService.deleteWineList(venueId, list._id);
      fetchItems(typeFilter, statusFilter, pagination.page, pagination.pageSize);
    } catch (err) {
      console.error('Failed to archive wine list', err);
      setFetchError(`Could not archive "${list.name}". Please try again.`);
    }
  };

  // ── Status chip menu ────────────────────────────────────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTarget, setMenuTarget] = useState<WineList | null>(null);
  const [statusChanging, setStatusChanging] = useState<string | null>(null);

  const openStatusMenu = (e: React.MouseEvent<HTMLElement>, list: WineList) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTarget(list);
  };

  const closeStatusMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  const handleStatusChange = async (newStatus: WineListStatus) => {
    if (!menuTarget) return;
    const target = menuTarget;
    closeStatusMenu();
    setStatusChanging(target._id);
    try {
      await wineListService.updateWineList(venueId, target._id, { status: newStatus });
      fetchItems(typeFilter, statusFilter, pagination.page, pagination.pageSize);
    } catch (err) {
      console.error('Failed to update status', err);
      setFetchError(`Could not update status for "${target.name}". Please try again.`);
    } finally {
      setStatusChanging(null);
    }
  };

  const hasActiveFilters = Boolean(typeFilter || statusFilter);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header — Phase 4.I rebranded to "Legacy Wine Lists" to make the
            compatibility-only nature of this page unambiguous. The "New Wine
            List" CTA is suppressed in the legacy header because new wine
            lists should be authored from the Menus surface. The button still
            exists below the reveal toggle for operators who explicitly need
            it during migration. */}
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ListIcon sx={{ color: theme.palette.text.secondary }} />
            <Typography variant="h6" fontWeight={600}>Legacy Wine Lists</Typography>
          </Box>
        </Box>

        {/* Phase 4.I migration panel — directs the operator to Menus first,
            keeps the legacy table behind an opt-in reveal so this page reads
            as a compatibility surface, not a primary authoring tool. */}
        <Box sx={{ px: 3, py: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Alert severity="info" sx={{ borderRadius: 1, mb: 2 }}>
            Wine lists are now managed through Menus. This legacy page remains available only for compatibility during migration.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(`/admin/venues/${venueId}/menus`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Go to Menus
            </Button>
            <Button
              variant="text"
              size="small"
              startIcon={showLegacyTable ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowLegacyTable(s => !s)}
              sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
            >
              {showLegacyTable ? 'Hide legacy wine lists' : 'View legacy wine lists'}
            </Button>
          </Box>
        </Box>

        {showLegacyTable && (<>

        {/* Fetch error */}
        {fetchError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setFetchError('')} sx={{ borderRadius: 1 }}>{fetchError}</Alert>
          </Box>
        )}

        {/* Legacy actions strip — "New Wine List" available here for
            operators who explicitly opened the legacy view. */}
        <Box sx={{ px: 3, pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate(`${base}/create`)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            New Wine List
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={e => { setTypeFilter(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}>
              <MenuItem value="">All types</MenuItem>
              {WINE_LIST_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}>
              <MenuItem value="">Active &amp; Draft</MenuItem>
              {WINE_LIST_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              <MenuItem value="all">All statuses</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Name</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Type</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Status</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Description</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 7, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <ListIcon sx={{ fontSize: 44, mb: 1.5, opacity: 0.25 }} />
                      <Typography variant="body2" fontWeight={500} gutterBottom>
                        {hasActiveFilters ? 'No wine lists match the current filters.' : 'No wine lists yet.'}
                      </Typography>
                      {!hasActiveFilters && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => navigate(`${base}/create`)}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Create first wine list
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map(list => (
                  <TableRow
                    key={list._id}
                    hover
                    onClick={() => navigate(`${base}/${list._id}`)}
                    sx={{ cursor: 'pointer', opacity: list.status === 'ARCHIVED' ? 0.55 : 1 }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{list.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {list.type.replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {list.status === 'ARCHIVED' ? (
                        <Chip
                          label="ARCHIVED"
                          size="small"
                          color="warning"
                          sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                      ) : statusChanging === list._id ? (
                        <CircularProgress size={16} sx={{ ml: 0.5 }} />
                      ) : (
                        <Chip
                          label={list.status}
                          size="small"
                          color={STATUS_COLORS[list.status]}
                          onClick={e => openStatusMenu(e, list)}
                          onDelete={e => openStatusMenu(e, list)}
                          deleteIcon={<DropdownIcon />}
                          sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {list.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => navigate(`${base}/edit/${list._id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {list.status !== 'ARCHIVED' && (
                        <IconButton size="small" color="warning" onClick={e => handleArchive(e, list)}>
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={e => setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
          rowsPerPageOptions={[10, 25, 50]}
        />

        </>)}
      </Paper>

      {/* Status change menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeStatusMenu}
        slotProps={{ paper: { elevation: 3, sx: { minWidth: 140, borderRadius: 1.5 } } }}
      >
        {(['DRAFT', 'ACTIVE'] as WineListStatus[])
          .filter(s => s !== menuTarget?.status)
          .map(s => (
            <MenuItem
              key={s}
              onClick={() => handleStatusChange(s)}
              sx={{ fontSize: '0.85rem', gap: 1.5 }}
            >
              <Chip
                label={s}
                size="small"
                color={STATUS_COLORS[s]}
                sx={{ fontSize: '0.7rem', height: 20, pointerEvents: 'none' }}
              />
              Set {s === 'ACTIVE' ? 'Active' : 'Draft'}
            </MenuItem>
          ))
        }
      </Menu>
    </Box>
  );
};

export default WineListList;
